import { type FC, useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { ReservationState } from '../../../modules/enums/types';
import { useNotifications } from '../../../modules/hooks';
import { useAccessCodes } from '../../Dashboards/TemporaryAccessCodes/useAccessCodes';
import { ReservationPickerTable } from './ReservationPickerTable';

interface PromoteModalProps {
  show: boolean;
  uuid?: string;
  handleClose: () => void;
  onSaved?: () => void;
}

// Confirmed reservations only — they haven't checked in yet, so they have no assigned code.
const PROMOTE_STATES = [ReservationState.CONFIRMED];

/**
 * Promotes a temporary access code to a reservation code. The admin picks the reservation
 * (searchable single-select table); the backend attaches its locks and re-times the code.
 */
export const PromoteModal: FC<PromoteModalProps> = ({
  show,
  uuid,
  handleClose,
  onSaved,
}) => {
  const { setSimpleToasts } = useNotifications()!;
  const accessCodes = useAccessCodes();

  const [reservationId, setReservationId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) setReservationId('');
  }, [show]);

  const handleSave = async () => {
    if (!uuid || !reservationId) return;
    setSaving(true);
    try {
      await accessCodes.promote(uuid, { reservationId });
      setSimpleToasts({ type: 'success', message: 'Access code promoted', show: true });
      onSaved?.();
      handleClose();
    } catch {
      setSimpleToasts({
        type: 'danger',
        message: 'Failed to promote access code',
        show: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Promote to reservation</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted">
          Link this temporary code to a reservation. Its locks and time window are taken
          from the reservation.
        </p>
        <ReservationPickerTable
          active={show}
          states={PROMOTE_STATES}
          emptyLabel="No reservations available to promote to."
          onSelect={(selection) => setReservationId(selection?.reservationId ?? '')}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button className="cancel-button" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          className="save-button"
          onClick={handleSave}
          disabled={saving || !reservationId}
        >
          {saving ? 'Promoting…' : 'Promote'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
